import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Card,
  CardBody,
  Button,
  Container,
  Modal,
  Alert,
  Spinner,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { LOGIN, SETTINGS, LOANDUES, MODULEDUES } from "../../services/actions";
import {
  SignOut,
  Shield,
  Warning,
  CheckCircle,
  Info,
} from "@phosphor-icons/react";
import { useNavigate, useLocation } from "react-router-dom";
import { CardContent, Typography, Grid } from "@mui/material";

import Badge from "react-bootstrap/Badge";
import termsvg from "./term.svg";
import Flipcard from "./Flipcard";
import "../../App.css";
import ResponsiveText from "../../components/ResponsiveText";
import AppLayout from "../../layouts/AppLayout";
import LoanDetailsModal from "../../components/LoanDetailsModal";
import { ClearCache } from "../../components/ClearCache";

/**
 * Dashboard Component - Main Application Interface
 *
 * This component serves as the primary interface after successful authentication.
 * It implements comprehensive security measures and user session management.
 *
 * Security Features:
 * - JWT token validation and refresh
 * - Multi-tab session enforcement with timestamp tracking
 * - Session timeout handling
 * - Secure API communication
 * - User activity tracking
 * - Automatic logout on security violations
 * - Latest tab priority system
 *
 * Functionality:
 * - Loan account management
 * - Payment processing
 * - Transaction history
 * - User profile management
 */

// ==================== AUTHENTICATION & SECURITY HOOKS ====================

/**
 * Enhanced Token Management Hook with Multi-Tab Session Enforcement
 *
 * Handles all aspects of JWT token management including:
 * - Token validation and verification
 * - Multi-tab session enforcement with timestamp tracking
 * - Latest tab priority system
 * - Automatic token refresh
 * - Token expiration handling
 * - Secure storage management
 */
const useTokenManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [authState, setAuthState] = useState({
    token: null,
    isValidated: false,
    isExpired: false,
    expiresAt: null,
    sessionId: null,
    isActiveTab: true,
    isLatestTab: true,
    isInitializing: true,  // NEW: Add loading state to prevent flash
  });

  // Generate unique session ID with timestamp for this tab
  const sessionIdRef = useRef(null);
  const tabCreatedAtRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const storageListenerRef = useRef(null);

  useEffect(() => {
    if (!sessionIdRef.current) {
      const timestamp = Date.now();
      sessionIdRef.current = `tab_${timestamp}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      tabCreatedAtRef.current = timestamp;
      
      console.log("🆔 [Dashboard] Generated unique tab session ID:", {
        sessionId: sessionIdRef.current,
        createdAt: new Date(timestamp).toISOString()
      });
    }
  }, []);

  /**
   * Enhanced multi-tab session enforcement system with timestamp tracking
   */
  const initializeMultiTabProtection = useCallback(() => {
    const currentTabId = sessionIdRef.current;
    const currentTabCreatedAt = tabCreatedAtRef.current;
    const ACTIVITY_WINDOW = 8000; // 8 seconds
    const HEARTBEAT_INTERVAL = 3000; // 3 seconds

    console.log("🔒 [Dashboard] Initializing enhanced multi-tab protection:", {
      tabId: currentTabId,
      createdAt: new Date(currentTabCreatedAt).toISOString()
    });

    const checkTabConflict = () => {
      try {
        // Get all active tabs from cache
        const activeTabsData = localStorage.getItem("activeTabs");
        const activeTabs = activeTabsData ? JSON.parse(activeTabsData) : {};
        const currentTime = Date.now();

        console.log("🔍 [Dashboard] Tab conflict check:", {
          currentTab: currentTabId,
          currentTabCreatedAt: new Date(currentTabCreatedAt).toISOString(),
          existingTabs: Object.keys(activeTabs),
          tabsData: Object.entries(activeTabs).map(([id, data]) => ({
            id,
            createdAt: new Date(data.createdAt).toISOString(),
            lastActivity: new Date(data.lastActivity).toISOString()
          }))
        });

        // Clean up expired/inactive tabs (older than ACTIVITY_WINDOW)
        const cleanedTabs = {};
        let latestTabId = null;
        let latestTabCreatedAt = 0;

        // First pass: identify active tabs and find the latest one
        Object.entries(activeTabs).forEach(([tabId, tabData]) => {
          const timeSinceActivity = currentTime - tabData.lastActivity;
          
          if (timeSinceActivity < ACTIVITY_WINDOW) {
            cleanedTabs[tabId] = tabData;
            
            // Track the latest tab by creation time
            if (tabData.createdAt > latestTabCreatedAt) {
              latestTabCreatedAt = tabData.createdAt;
              latestTabId = tabId;
            }
          }
        });

        // Add current tab to active tabs
        cleanedTabs[currentTabId] = {
          createdAt: currentTabCreatedAt,
          lastActivity: currentTime,
          sessionId: currentTabId
        };

        // Determine if current tab is the latest
        const isCurrentTabLatest = currentTabCreatedAt >= latestTabCreatedAt;
        
        if (isCurrentTabLatest) {
          latestTabId = currentTabId;
        }

        console.log("📊 [Dashboard] Tab analysis:", {
          currentTabIsLatest: isCurrentTabLatest,
          latestTabId,
          totalActiveTabs: Object.keys(cleanedTabs).length,
          shouldShowAlert: !isCurrentTabLatest && Object.keys(cleanedTabs).length > 1
        });

        // Update localStorage with cleaned data
        localStorage.setItem("activeTabs", JSON.stringify(cleanedTabs));
        localStorage.setItem("latestTabId", latestTabId);
        localStorage.setItem("latestTabCreatedAt", latestTabCreatedAt.toString());

        // If current tab is not the latest and there are multiple tabs
        if (!isCurrentTabLatest && Object.keys(cleanedTabs).length > 1) {
          console.log("⚠️ [Dashboard] Current tab is not latest - triggering security alert");
          
          setAuthState((prev) => ({
            ...prev,
            isActiveTab: false,
            isLatestTab: false,
          }));

          return false;
        }

        // Current tab is the latest - mark as active
        setAuthState((prev) => ({
          ...prev,
          sessionId: currentTabId,
          isActiveTab: true,
          isLatestTab: true,
        }));

        return true;

      } catch (error) {
        console.error("❌ [Dashboard] Error in tab conflict check:", error);
        return true; // Fail safe - allow access if error occurs
      }
    };

    // Initial check
    const isValid = checkTabConflict();
    if (!isValid) return null;

    // Set up periodic heartbeat
    const heartbeatInterval = setInterval(() => {
      if (document.visibilityState === "visible") {
        const isStillValid = checkTabConflict();
        if (!isStillValid) {
          clearInterval(heartbeatInterval);
        }
      }
    }, HEARTBEAT_INTERVAL);

    // Listen for localStorage changes from other tabs
    const handleStorageChange = (e) => {
      if (e.key === "activeTabs" || e.key === "latestTabId") {
        console.log("🚨 [Dashboard] Tab data changed by another tab");
        
        // Delayed check to allow other tab to complete its updates
        setTimeout(() => {
          checkTabConflict();
        }, 500);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Update activity on user interactions
    const updateActivity = () => {
      if (authState.isActiveTab && authState.isLatestTab) {
        try {
          const activeTabsData = localStorage.getItem("activeTabs");
          const activeTabs = activeTabsData ? JSON.parse(activeTabsData) : {};
          
          if (activeTabs[currentTabId]) {
            activeTabs[currentTabId].lastActivity = Date.now();
            localStorage.setItem("activeTabs", JSON.stringify(activeTabs));
          }
        } catch (error) {
          console.error("❌ [Dashboard] Error updating activity:", error);
        }
      }
    };

    const activityEvents = ["click", "keydown", "scroll", "mousemove", "touchstart"];
    activityEvents.forEach((event) => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Page visibility change handler
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.log("👁️ [Dashboard] Tab became visible, checking conflicts...");
        setTimeout(() => {
          checkTabConflict();
        }, 500);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Store cleanup references
    heartbeatIntervalRef.current = heartbeatInterval;
    storageListenerRef.current = () => {
      clearInterval(heartbeatInterval);
      window.removeEventListener("storage", handleStorageChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      activityEvents.forEach((event) => {
        document.removeEventListener(event, updateActivity);
      });
    };

    return () => {
      if (storageListenerRef.current) {
        storageListenerRef.current();
      }
    };
  }, [authState.isActiveTab, authState.isLatestTab]);

  /**
   * Handle multi-tab logout (only for non-latest tabs)
   */
  const handleMultiTabLogout = useCallback(() => {
    console.log("🚪 [Dashboard] Multi-tab logout initiated for non-latest tab");

    // Remove only current tab from active tabs, don't clear all
    try {
      const activeTabsData = localStorage.getItem("activeTabs");
      const activeTabs = activeTabsData ? JSON.parse(activeTabsData) : {};
      
      // Remove current tab
      delete activeTabs[sessionIdRef.current];
      
      // Update localStorage
      localStorage.setItem("activeTabs", JSON.stringify(activeTabs));
      
      console.log("🗑️ [Dashboard] Removed current tab from active tabs list");
    } catch (error) {
      console.error("❌ [Dashboard] Error removing tab from active list:", error);
    }

    // Clear heartbeat and listeners for this tab
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    if (storageListenerRef.current) {
      storageListenerRef.current();
    }

    // Clear auth data for this tab only (don't clear tab tracking data)
    clearAuthData(false);

    // Navigate to login
    setTimeout(() => {
      navigate("/", {
        replace: true,
        state: {
          message: "Security Alert: This application is already open in another tab. You will be logged out for security reasons.",
          type: "error",
        },
      });
    }, 1000);
  }, [navigate]);

  /**
   * Validates token format - supports both JWT and simple tokens
   * @param {string} token - Token to validate
   * @returns {boolean} - Whether token is valid format
   */
  const validateTokenFormat = (token) => {
    if (!token || typeof token !== "string") {
      return false;
    }

    // Check for minimum length
    if (token.length < 10) {
      return false;
    }

    // Check if it's a JWT (has dots) or simple token
    const isJWT = token.split(".").length === 3;
    const isSimpleToken = /^[a-zA-Z0-9]+$/.test(token); // Alphanumeric token

    return isJWT || isSimpleToken;
  };

  /**
   * Checks if token is expired based on stored metadata
   * @param {string} token - Token to check
   * @returns {boolean} - Whether token is expired
   */
  const isTokenExpired = (token) => {
    try {
      const metadata = localStorage.getItem("tokenMetadata");
      if (!metadata) {
        console.log(
          "⚠️ [Dashboard] No token metadata found, assuming token is valid"
        );
        return false; // Don't assume expired if no metadata
      }

      const { timestamp, expiresIn } = JSON.parse(metadata);
      const isExpired = Date.now() > timestamp + expiresIn;

      console.log("🕐 [Dashboard] Token expiration check:", {
        isExpired,
        timeRemaining:
          Math.round((timestamp + expiresIn - Date.now()) / 1000 / 60) +
          " minutes",
      });

      return isExpired;
    } catch (error) {
      console.error("❌ [Dashboard] Error checking token expiration:", error);
      return false; // Don't assume expired on error
    }
  };

  /**
   * Initializes and validates authentication state
   */
  useEffect(() => {
    console.log("🔐 [Dashboard] Initializing token management system");

    const initializeAuth = () => {
      try {
        // Get token from multiple sources (navigation state takes precedence)
        const tokenFromState = location.state?.token;
        const tokenFromStorage = localStorage.getItem("authToken");

        console.log("🔍 [Dashboard] Token source analysis:", {
          fromNavigationState: !!tokenFromState,
          fromLocalStorage: !!tokenFromStorage,
          navigationStatePreview: tokenFromState
            ? `${tokenFromState.substring(0, 10)}...`
            : "None",
          storagePreview: tokenFromStorage
            ? `${tokenFromStorage.substring(0, 10)}...`
            : "None",
        });

        // Determine which token to use
        const currentToken = tokenFromState || tokenFromStorage;

        if (!currentToken) {
          console.log("❌ [Dashboard] No authentication token found");
          setAuthState({
            token: null,
            isValidated: false,
            isExpired: true,
            expiresAt: null,
            sessionId: null,
            isActiveTab: false,
            isLatestTab: false,
            isInitializing: false,
          });
          return;
        }

        // Validate token format
        if (!validateTokenFormat(currentToken)) {
          console.error("❌ [Dashboard] Invalid token format:", {
            tokenLength: currentToken.length,
            tokenType: typeof currentToken,
            hasSpecialChars: !/^[a-zA-Z0-9.]+$/.test(currentToken),
          });
          clearAuthData();
          return;
        }

        console.log("✅ [Dashboard] Token format validated successfully");

        // Check token expiration
        if (isTokenExpired(currentToken)) {
          console.error("❌ [Dashboard] Token has expired");
          setAuthState((prev) => ({ ...prev, isExpired: true, isInitializing: false }));
          return;
        }

        // Store token if it came from navigation state
        if (tokenFromState && !tokenFromStorage) {
          localStorage.setItem("authToken", tokenFromState);
          console.log("💾 [Dashboard] Token stored in localStorage");
        }

        // Update auth state
        setAuthState({
          token: currentToken,
          isValidated: true,
          isExpired: false,
          expiresAt: getTokenExpirationTime(),
          sessionId: null,
          isActiveTab: true,
          isLatestTab: true,
          isInitializing: false,
        });

        console.log(
          "✅ [Dashboard] Token validated and authentication state updated"
        );
      } catch (error) {
        console.error(
          "❌ [Dashboard] Error during token initialization:",
          error
        );
        clearAuthData();
      }
    };

    initializeAuth();
  }, [location]);

  // Initialize multi-tab protection when token is validated
  useEffect(() => {
    if (authState.token && authState.isValidated && sessionIdRef.current) {
      console.log("🛡️ [Dashboard] Setting up multi-tab protection...");
      const cleanup = initializeMultiTabProtection();
      return cleanup;
    }
  }, [authState.token, authState.isValidated, initializeMultiTabProtection]);

  /**
   * Gets token expiration time from metadata
   * @returns {number|null} - Expiration timestamp or null
   */
  const getTokenExpirationTime = () => {
    try {
      const metadata = localStorage.getItem("tokenMetadata");
      if (!metadata) return null;

      const { timestamp, expiresIn } = JSON.parse(metadata);
      return timestamp + expiresIn;
    } catch {
      return null;
    }
  };

  /**
   * Modified clear auth data - option to preserve tab tracking
   */
  const clearAuthData = useCallback((clearTabData = true) => {
    console.log("🗑️ [Dashboard] Clearing authentication data", { clearTabData });

    localStorage.removeItem("authToken");
    localStorage.removeItem("tokenMetadata");
    localStorage.removeItem("data");
    localStorage.removeItem("sessionStartTime");
    localStorage.removeItem("lastActivity");

    // Only clear tab tracking data if explicitly requested (during full logout)
    if (clearTabData) {
      localStorage.removeItem("activeTabs");
      localStorage.removeItem("latestTabId");
      localStorage.removeItem("latestTabCreatedAt");
      console.log("🗑️ [Dashboard] Tab tracking data cleared");
    }

    // Clear intervals and listeners
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    if (storageListenerRef.current) {
      storageListenerRef.current();
    }

    setAuthState({
      token: null,
      isValidated: false,
      isExpired: false,
      expiresAt: null,
      sessionId: null,
      isActiveTab: false,
      isLatestTab: false,
      isInitializing: false,
    });
  }, []);

  /**
   * Refreshes authentication token (placeholder for future implementation)
   */
  const refreshToken = useCallback(async () => {
    try {
      console.log("🔄 [Dashboard] Token refresh requested");
      console.log("⚠️ [Dashboard] Token refresh not yet implemented");
      return false;
    } catch (error) {
      console.error("❌ [Dashboard] Token refresh failed:", error);
      clearAuthData();
      return false;
    }
  }, [clearAuthData]);

  return {
    ...authState,
    clearAuthData,
    refreshToken,
    handleMultiTabLogout,
    tabCreatedAt: tabCreatedAtRef.current,
  };
};

/**
 * Enhanced User Data Management Hook
 */
const useUserData = () => {
  return useMemo(() => {
    console.log("👤 [Dashboard] Loading user data from storage");

    try {
      const userData = localStorage.getItem("data");

      if (!userData || userData === "null" || userData === "undefined") {
        console.log("❌ [Dashboard] No valid user data found in storage");
        return null;
      }

      const parsedData = JSON.parse(userData);
      console.log("📋 [Dashboard] Raw user data loaded:", {
        hasCustomerID: !!parsedData.customerID,
        hasCustomerName: !!parsedData.customerName,
        hasCellNumber: !!parsedData.cellNumber,
        dataKeys: Object.keys(parsedData),
      });

      const requiredFields = ["customerID", "customerName", "cellNumber"];
      const missingFields = requiredFields.filter(
        (field) => !parsedData[field]
      );

      if (missingFields.length > 0) {
        console.error("❌ [Dashboard] User data missing required fields:", {
          missingFields,
          availableFields: Object.keys(parsedData),
        });
        return null;
      }

      console.log("✅ [Dashboard] User data validated successfully:", {
        customerID: parsedData.customerID,
        customerName: parsedData.customerName,
        cellNumber: parsedData.cellNumber.replace(/(\d{6})(\d{4})/, "$1****"),
      });

      return parsedData;
    } catch (error) {
      console.error("❌ [Dashboard] Error parsing user data:", error);
      localStorage.removeItem("data");
      return null;
    }
  }, []);
};

/**
 * Enhanced Session Management Hook for cache and version control
 */
const useSessionManagement = (user, authState) => {
  const [clearCache, setClearCache] = useState(false);

  useEffect(() => {
    if (!user || !authState.isActiveTab || !authState.isLatestTab) return;

    console.log("⚙️ [Dashboard] Checking session management requirements");

    const checkVersionUpdate = () => {
      const storedVersion = sessionStorage.getItem("version");
      const userVersion = user?.version;

      if (
        userVersion &&
        (!storedVersion || Number(userVersion) > Number(storedVersion))
      ) {
        console.log("🔄 [Dashboard] Version update detected:", {
          old: storedVersion,
          new: userVersion,
        });
        sessionStorage.setItem("version", userVersion);
        setClearCache(true);
      }
    };

    const checkSessionUpdate = () => {
      const storedSessionId = sessionStorage.getItem("session_id");
      const userSessionId = user?.session_id;

      if (
        userSessionId &&
        (!storedSessionId || Number(userSessionId) > Number(storedSessionId))
      ) {
        console.log("🔄 [Dashboard] Session update detected:", {
          old: storedSessionId,
          new: userSessionId,
        });
        sessionStorage.setItem("session_id", userSessionId);
        setClearCache(true);
      }
    };

    checkVersionUpdate();
    checkSessionUpdate();
  }, [user, authState.isActiveTab, authState.isLatestTab]);

  return { clearCache };
};

/**
 * Back Button Handler Hook
 */
const useBackButtonHandler = () => {
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const handleBackButton = (event) => {
      event.preventDefault();
      setShowModal(true);
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handleBackButton);

    return () => window.removeEventListener("popstate", handleBackButton);
  }, []);

  return { showModal, setShowModal };
};

/**
 * Multi-Tab Warning Modal Component
 */
const MultiTabWarningModal = ({ show, onConfirm }) => {
  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    if (show && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      onConfirm();
    }
  }, [show, countdown, onConfirm]);

  return (
    <Modal show={show} backdrop="static" keyboard={false} centered>
      <Modal.Header>
        <Modal.Title className="text-danger">
          <Warning size={24} className="me-2" />
          Multi-Tab Session Detected!
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="danger">
          <h6><strong>🚨 Security Alert</strong></h6>
          <p>Multiple browser tabs with this application have been detected.</p>
          <p><strong>For your security, only one tab can be active at a time.</strong></p>
          <p>This session will be terminated in <strong className="text-danger">{countdown} seconds</strong>.</p>
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={onConfirm}>
          Close Now ({countdown}s)
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ==================== MAIN COMPONENT ====================

export default function Dashboard() {
  console.log("🏗️ [Dashboard] Component initialization started");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ==================== AUTHENTICATION & SECURITY ====================

  const authState = useTokenManagement();
  const user = useUserData();

  // ==================== SESSION & STATE MANAGEMENT ====================

  const { clearCache } = useSessionManagement(user, authState);
  const { showModal, setShowModal } = useBackButtonHandler();

  // Redux state selectors
  const loanState = useSelector((state) => state?.loanduesState);
  const settingsState = useSelector((state) => state?.settingsState);
  const moduleduesState = useSelector((state) => state.moduleduesState?.data);

  // Component state
  const [selectedLoans, setSelectedLoans] = useState(null);
  const [showDuesModal, setShowDuesModal] = useState(false);
  const [toggle, setToggle] = useState(0);
  const [isInitializing, setIsInitializing] = useState(true);
  const [showMultiTabWarning, setShowMultiTabWarning] = useState(false);

  // Form management
  const { handleSubmit } = useForm({
    defaultValues: { username: "admin@gmail.com", password: "Admin@123" },
    mode: "all",
  });

  // Computed values
  const loans = useMemo(() => loanState?.data?.data, [loanState?.data]);

  // Handle multi-tab detection
  useEffect(() => {
    if (!authState.isActiveTab && authState.token) {
      console.log("🚨 [Dashboard] Multi-tab conflict detected - showing warning");
      setShowMultiTabWarning(true);
    }
  }, [authState.isActiveTab, authState.token]);

  // Handle multi-tab warning confirmation
  const handleMultiTabConfirm = useCallback(() => {
    console.log("👋 [Dashboard] User confirmed multi-tab logout");
    setShowMultiTabWarning(false);
    if (authState.handleMultiTabLogout) {
      authState.handleMultiTabLogout();
    }
  }, [authState]);

  // ==================== DEBUG MONITORING ====================

  // Monitor Redux state changes
  useEffect(() => {
    console.log("📊 [Dashboard] Loan state updated:", {
      loading: loanState?.loading,
      hasData: !!loanState?.data,
      error: loanState?.error,
      dataKeys: loanState?.data ? Object.keys(loanState.data) : [],
      dataPreview: loanState?.data
        ? {
            hasAccTypes: !!loanState?.data?.data?.acc_types,
            hasAccounts: !!loanState?.data?.data?.accounts,
            accTypesCount: loanState?.data?.data?.acc_types?.length || 0,
          }
        : null,
    });
  }, [loanState]);

  useEffect(() => {
    console.log("⚙️ [Dashboard] Settings state updated:", {
      loading: settingsState?.loading,
      hasData: !!settingsState?.data,
      error: settingsState?.error,
      dataKeys: settingsState?.data ? Object.keys(settingsState.data) : [],
    });
  }, [settingsState]);

  // ==================== COMPONENT LIFECYCLE ====================

  /**
   * Initialize dashboard data and services
   */
  useEffect(() => {
    const initializeDashboard = () => {
      // Validate prerequisites including active tab status
      if (
        !user?.customerID ||
        !authState.token ||
        !authState.isValidated ||
        !authState.isActiveTab ||
        !authState.isLatestTab
      ) {
        console.log(
          "⏳ [Dashboard] Prerequisites not met for initialization:",
          {
            hasUser: !!user,
            hasCustomerID: !!user?.customerID,
            hasToken: !!authState.token,
            isTokenValidated: authState.isValidated,
            isActiveTab: authState.isActiveTab,
            isLatestTab: authState.isLatestTab,
          }
        );
        setIsInitializing(false);
        return;
      }

      console.log("🚀 [Dashboard] Starting dashboard initialization");
      console.log("🆔 [Dashboard] Customer ID:", user.customerID);
      console.log(
        "🔑 [Dashboard] Token:",
        authState.token.substring(0, 10) + "..."
      );
      console.log("📱 [Dashboard] Session ID:", authState.sessionId);

      // Reset loan data
      dispatch({ type: `${LOANDUES}_RESET` });

      // Fetch loan data with authentication headers
      dispatch({
        type: `${LOANDUES}_GET`,
        payload: user.customerID,
        options: {
          headers: {
            Authorization: `Token ${authState.token}`,
            "Content-Type": "application/json",
          },
        },
      });

      // Fetch settings with authentication headers
      dispatch({
        type: `${SETTINGS}_GET`,
        payload: 1,
        options: {
          headers: {
            Authorization: `Token ${authState.token}`,
            "Content-Type": "application/json",
          },
        },
      });

      console.log("📡 [Dashboard] API calls dispatched");
      setIsInitializing(false);
    };

    initializeDashboard();
  }, [
    dispatch,
    user?.customerID,
    authState.token,
    authState.isValidated,
    authState.isActiveTab,
    authState.isLatestTab,
  ]);

  /**
   * Process settings updates
   */
  useEffect(() => {
    if (!settingsState?.data?.data?.main) return;

    try {
      const mainStr = settingsState.data.data.main;
      const main = typeof mainStr === "string" ? JSON.parse(mainStr) : mainStr;
      localStorage.setItem("settings", JSON.stringify(main));

      console.log("⚙️ [Dashboard] Application settings updated successfully");
    } catch (error) {
      console.error("❌ [Dashboard] Error processing settings:", error);
    }
  }, [settingsState]);

  // ==================== EVENT HANDLERS ====================

  /**
   * Secure sign out with complete cleanup
   */
  const handleSignOut = useCallback(() => {
    console.log("🚪 [Dashboard] Initiating secure sign out process");

    try {
      // Clear all data including tab tracking (full logout)
      authState.clearAuthData(true);
      localStorage.removeItem("settings");
      localStorage.removeItem("sessionHeartbeat");
      localStorage.removeItem("sessionPersistent");
      localStorage.removeItem("persistedUserData");
      localStorage.removeItem("persistedAuthToken");
      sessionStorage.clear();

      console.log("✅ [Dashboard] Sign out completed successfully");

      navigate("/", {
        replace: true,
        state: { message: "You have been signed out successfully" },
      });
    } catch (error) {
      console.error("❌ [Dashboard] Error during sign out:", error);
      window.location.href = "/";
    }
  }, [authState, navigate]);

  /**
   * Handle modal leave confirmation
   */
  const handleLeave = useCallback(() => {
    setShowModal(false);
    handleSignOut();
  }, [handleSignOut, setShowModal]);

  /**
   * Handle payment initiation
   */
  const handlePayment = useCallback(
    (payAmount) => {
      console.log("💳 [Dashboard] Payment initiated:", {
        amount: payAmount,
        customerID: user?.customerID,
        timestamp: new Date().toISOString(),
      });
    },
    [user?.customerID]
  );

  /**
   * Fetch detailed loan information
   */
  const fetchLoanDetails = useCallback(
    async (accNumber, moduleCode, loan) => {
      if (!accNumber || !moduleCode) {
        console.error(
          "❌ [Dashboard] Invalid parameters for loan details fetch"
        );
        return;
      }

      console.log("📋 [Dashboard] Fetching loan details:", {
        accountNumber: accNumber,
        moduleCode,
        loanType: loan?.loanTypeDesc,
      });

      try {
        setShowDuesModal(loan);

        dispatch({
          type: `${MODULEDUES}_POST`,
          payload: {
            uniqueAccountNumber: accNumber,
            moduleCode: moduleCode,
          },
          options: {
            headers: {
              Authorization: `Token ${authState.token}`,
              "Content-Type": "application/json",
            },
          },
        });

        console.log("✅ [Dashboard] Loan details request dispatched");
      } catch (error) {
        console.error("❌ [Dashboard] Error fetching loan details:", error);
        setShowDuesModal(false);
      }
    },
    [dispatch, authState.token]
  );

  /**
   * Handle financial product selection
   */
  const handleProductSelection = useCallback(
    (key) => {
      if (!loans?.accounts?.[key]) {
        console.warn("⚠️ [Dashboard] No accounts found for product key:", key);
        return;
      }

      console.log("📊 [Dashboard] Financial product selected:", {
        productKey: key,
        accountCount: loans.accounts[key].length,
      });

      setSelectedLoans([...loans.accounts[key]]);
      setToggle(key);
    },
    [loans]
  );

  /**
   * Navigate to transaction history
   */
  const navigateToHistory = useCallback(() => {
    console.log("📜 [Dashboard] Navigating to transaction history");

    navigate("/history/", {
      state: {
        index: 2,
        token: authState.token,
        customerID: user?.customerID,
      },
    });
  }, [navigate, authState.token, user?.customerID]);

  // ==================== SECURITY VALIDATIONS ====================

  // Show loading during initialization to prevent flash
  if (authState.isInitializing) {
    return (
      <AppLayout>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
          <div className="text-center">
            <Spinner animation="border" className="me-2" />
            <span>Loading dashboard...</span>
          </div>
        </Container>
      </AppLayout>
    );
  }

  // Check for user data after initialization
  if (!user) {
    return (
      <AppLayout>
        <Container
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "50vh" }}
        >
          <Card className="w-100" style={{ maxWidth: "500px" }}>
            <CardBody className="text-center p-4">
              <Warning size={48} className="text-warning mb-3" />
              <h4 className="text-warning mb-3">Session Data Missing</h4>
              <p className="mb-4">
                Your session data is not available. Please sign in again to
                continue.
              </p>
              <Button
                variant="primary"
                onClick={() => navigate("/", { replace: true })}
              >
                Return to Sign In
              </Button>
              <div className="mt-3">
                <small className="text-muted">
                  Debug:{" "}
                  {localStorage.getItem("data")
                    ? "Data exists but invalid"
                    : "No data found"}
                </small>
              </div>
            </CardBody>
          </Card>
        </Container>
      </AppLayout>
    );
  }

  // Check authentication after initialization is complete
  if (!authState.token || !authState.isValidated) {
    return (
      <AppLayout>
        <Container
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "50vh" }}
        >
          <Card className="w-100" style={{ maxWidth: "500px" }}>
            <Card.Body className="text-center p-4">
              <Shield size={48} className="text-danger mb-3" />
              <h4 className="text-danger mb-3">Authentication Required</h4>
              <p className="mb-4">
                {authState.isExpired
                  ? "Your session has expired."
                  : "Invalid authentication credentials."}
              </p>
              <Button
                variant="primary"
                onClick={() => {
                  // Clear all storage and force reload
                  localStorage.clear();
                  sessionStorage.clear();
                  window.location.href = "/";
                }}
              >
                Sign In Again
              </Button>
            </Card.Body>
          </Card>
        </Container>
      </AppLayout>
    );
  }

  // Show inactive/non-latest tab warning
  if (!authState.isActiveTab || !authState.isLatestTab) {
    return (
      <AppLayout>
        <Container
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "50vh" }}
        >
          <Card className="w-100" style={{ maxWidth: "500px" }}>
            <Card.Body className="text-center p-4">
              <Warning size={48} className="text-danger mb-3" />
              <h4 className="text-danger mb-3">🚨 Security Alert</h4>
              <Alert variant="danger">
                <p className="mb-3">
                  <strong>This application is already open in another tab.</strong>
                </p>
                <p className="mb-3">
                  You will be logged out for security reasons.
                </p>
                <p className="small text-muted">
                  Tab ID: {authState.sessionId?.substring(0, 15)}...<br/>
                  Created: {authState.tabCreatedAt ? new Date(authState.tabCreatedAt).toLocaleString() : "Unknown"}
                </p>
              </Alert>
              <Button
                variant="danger"
                onClick={() => {
                  // Force reload to trigger logout
                  window.location.reload();
                }}
              >
                Use Here
              </Button>
            </Card.Body>
          </Card>
        </Container>
      </AppLayout>
    );
  }

  // ==================== RENDER HELPERS ====================

  const renderFinancialProducts = () => {
    if (!loans?.acc_types) {
      return (
        <div className="text-center py-4">
          <Alert variant="info">
            <Info size={20} className="me-2" />
            {loanState?.loading
              ? "Loading financial products..."
              : "No financial products available at this time."}
          </Alert>
        </div>
      );
    }

    return (
      <Container className="d-flex justify-content-center mb-5">
        {loans.acc_types.map(({ key, label }) => (
          <Grid
            key={key}
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={2}
            spacing={1}
            sx={{
              display: "flex",
              mb: 3,
              px: { xs: 0.5, sm: 0.5, md: 0.5 },
              justifyContent: "center",
            }}
          >
            <Card
              border="primary"
              className={`loan-box ${toggle === key ? "loan-box-active" : ""}`}
              onClick={() => handleProductSelection(key)}
              style={{ cursor: "pointer" }}
            >
              <CardContent
                className=" text-primary text-center justify-content-center"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <img src={termsvg} width={40} alt="Financial Product" />
                <Typography
                  variant="caption"
                  className="text-primary mt-2 d-block"
                >
                  {label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Container>
    );
  };

  const renderSelectedLoans = () => {
    if (!Array.isArray(selectedLoans)) return null;

    if (selectedLoans.length === 0) {
      return (
        <Alert variant="warning" className="text-center">
          <Info size={20} className="me-2" />
          No accounts available for the selected product.
        </Alert>
      );
    }

    return selectedLoans.map((loan, i) => (
      <Card key={i} style={{ borderRadius: 10 }} className="mb-2">
        <CardBody style={{ border: "none" }}>
          <Flipcard
            onMoreClick={() =>
              fetchLoanDetails(loan.uniqueAccountNumber, loan.moduleCode, loan)
            }
            loanTypeDesc={loan.loanTypeDesc}
            Acc_number={loan.uniqueAccountNumber}
            Disb_amt={loan.disbursedAmount}
            Instmnt={loan.totalInstallments}
            Due_amt={loan.dueAmount}
            StartDate={new Date(loan.startDate).toLocaleDateString()}
            MaturityDate={new Date(loan.maturityDate).toLocaleDateString()}
            onPay={handlePayment}
            pledgeNetWeight={loan.pledgeNetWeight}
            Loan={loan}
            moduleCode={loan.moduleCode}
            RD_redemptionDate={new Date(
              loan.redemptionDate
            ).toLocaleDateString()}
            RD_installmentAmount={loan.installmentAmount}
            RD_depositType={loan.depositType}
            interestAmountDue={loan.interestAmountDue}
            depositType={loan.depositType}
            currentBalance={loan.currentBalance}
          />
        </CardBody>
      </Card>
    ));
  };

  // ==================== MAIN RENDER ====================

  return (
    <>
      <AppLayout>
        <div className="w-100 px-4">
          {/* Application Header */}
          <div className="d-flex justify-content-between align-items-center mb-4 mt-3">
            <h1 className="app-title mb-0">Payment Portal</h1>
            <div className="d-flex align-items-center gap-2">
              <Badge bg={authState.isActiveTab && authState.isLatestTab ? "success" : "warning"}>
                <CheckCircle size={14} className="me-1" />
                {authState.isActiveTab && authState.isLatestTab ? "Active" : "Inactive Tab"}
              </Badge>
              <SignOut
                size={20}
                className="text-danger cursor-pointer"
                onClick={handleSignOut}
                title="Sign Out"
              />
            </div>
          </div>

          {/* Loading State */}
          {(isInitializing || loanState?.loading) && (
            <div className="text-center py-4">
              <Spinner animation="border" className="me-2" />
              <span>Loading your dashboard...</span>
            </div>
          )}

          {/* Main Content */}
          {!isInitializing && (
            <form autoComplete="false" onSubmit={handleSubmit(() => {})}>
              {/* User Welcome Section */}
              <div className="my-4">
                <h2>
                  Welcome,{" "}
                  <span className="text-primary">{user.customerName}</span>
                </h2>
                <div className="text-muted small mb-3">
                  Customer ID: {user.customerID} | Phone:{" "}
                  {user.cellNumber.replace(/(\d{6})(\d{4})/, "****$1")} |
                  {/* Location: {user.district}, {user.state} */}
                </div>
                <ResponsiveText>
                  <h3 className="fw-normal">
                    Select a Product and Account to make a payment
                  </h3>
                </ResponsiveText>
              </div>

              {/* Financial Products Section */}
              <h6 className="mt-4 mb-4 app-title2 text-start">
                Financial Products
              </h6>
              {renderFinancialProducts()}
            </form>
          )}

          {/* Selected Loans Display */}
          {renderSelectedLoans()}

          {/* Transaction History Link */}
          <div
            className="text-primary cursor-pointer mt-4 py-2 text-center"
            style={{ marginBottom: "50px" }}
            onClick={navigateToHistory}
          >
            📜 View Transaction History
          </div>

          {/* Security Footer */}
          <div
            className="text-center text-muted py-3"
            style={{ marginBottom: "80px" }}
          >
            <small>
              🔒 Session: {authState.sessionId?.substring(0, 12)}... |
              Status: {authState.isActiveTab && authState.isLatestTab ? "Latest Tab Active" : "Inactive Tab"} |
              Created: {authState.tabCreatedAt ? new Date(authState.tabCreatedAt).toLocaleTimeString() : "Unknown"}
            </small>
          </div>
        </div>

        {/* Navigation Confirmation Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Navigation</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Are you sure you want to leave this page? You will be signed out
              for security.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Stay Here
            </Button>
            <Button variant="danger" onClick={handleLeave}>
              Leave & Sign Out
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Multi-Tab Warning Modal */}
        <MultiTabWarningModal
          show={showMultiTabWarning}
          onConfirm={handleMultiTabConfirm}
        />

        {/* Loan Details Modal */}
        <LoanDetailsModal
          details={moduleduesState?.data}
          Acc_number={showDuesModal?.uniqueAccountNumber}
          show={showDuesModal}
          onHide={() => setShowDuesModal(false)}
        />

        {/* Cache Clear Component */}
        {clearCache && <ClearCache />}
      </AppLayout>
    </>
  );
}
